import invariant from 'invariant'

import { Fragment, createContext, useState, ReactNode, useContext } from 'react'
import i18n from '@signavio/i18n'
import { useStyles, Title4, Modal } from '@signavio/ui'

import { DevTool } from '@signavio/effektif-commons'
import { Checkbox, DangerHint, FlatButton } from '@signavio/ui'
import { Stack } from '@signavio/ui/core'

import { useApi } from '../../api'
import apiTypes from '../../apiTypes'

import { useCallbackWhenRequestFulfills, useUser } from '../../hooks'
import { FeatureT } from '../../../types'

export const FeaturesContext = createContext<string[]>([])

type FeaturesProps = {
  children: ReactNode
}

export const useActiveFeatures = () => {
  return useContext(FeaturesContext)
}

export default function Features({ children }: FeaturesProps) {
  const [request] = useApi(apiTypes.FEATURES)
  const [activeFeatures, setActiveFeatures] = useState<string[]>([])

  useCallbackWhenRequestFulfills(request, (features: FeatureT[]) => {
    setActiveFeatures(features.map(({ key }) => key))
  })

  const user = useUser()

  if (request.rejected) {
    return <DangerHint>{request.reason}</DangerHint>
  }

  if (request.pending || !request.value) {
    return null
  }

  return (
    <FeaturesContext.Provider value={activeFeatures}>
      {children}

      {user.systemAdmin && (
        <DevTool>
          <ManageFeatures
            activeFeatures={activeFeatures}
            onToggleFeature={(feature: string) => {
              if (activeFeatures.includes(feature)) {
                setActiveFeatures(
                  activeFeatures.filter(
                    (activeFeature) => activeFeature !== feature
                  )
                )
              } else {
                setActiveFeatures([...activeFeatures, feature])
              }
            }}
          />
        </DevTool>
      )}
    </FeaturesContext.Provider>
  )
}

function ManageFeatures({
  activeFeatures,
  onToggleFeature,
}: {
  activeFeatures: string[]
  onToggleFeature: (feature: string) => void
}) {
  const [showFeatures, setShowFeatures] = useState(false)
  const [featuresRequest] = useApi(apiTypes.ADMIN_FEATURES)

  const styles = useStyles(defaultStyles, {})

  if (!featuresRequest.value) {
    return (
      <FlatButton small disabled onClick={() => {}}>
        {i18n('Manage features')}
      </FlatButton>
    )
  }

  const features: FeatureT[] = featuresRequest.value

  const groups = features.reduce(
    (result: Record<string, string[]>, { key }: { key: string }) => {
      const [group, name] = key.split('.')

      if (result[group]) {
        return {
          ...result,
          [group]: [...result[group], name],
        }
      }

      return {
        ...result,
        [group]: [name],
      }
    },
    {}
  )

  return (
    <>
      <FlatButton small onClick={() => setShowFeatures(true)}>
        {i18n('Manage features')}
      </FlatButton>

      <Modal
        open={showFeatures}
        title={i18n('Features')}
        onRequestClose={() => setShowFeatures(false)}
      >
        <Stack spacing="small">
          {Object.entries(groups).map(([group, features]) => {
            invariant(Array.isArray(features), 'Features must be an array.')

            return (
              <Fragment key={group}>
                <Title4>{group}</Title4>

                <div {...styles}>
                  {features.map((key) => {
                    invariant(
                      typeof key === 'string',
                      `Expected "string" key but got "${typeof key}".`
                    )

                    return (
                      <Checkbox
                        checked={activeFeatures.includes(`${group}.${key}`)}
                        onChange={() => onToggleFeature(`${group}.${key}`)}
                        key={key}
                      >
                        {key}
                      </Checkbox>
                    )
                  })}
                </div>
              </Fragment>
            )
          })}
        </Stack>
      </Modal>
    </>
  )
}

const defaultStyles = {
  display: 'grid',

  gridTemplateColumns: '1fr 1fr',
}
